// 1Rotary — Datasets for the new strategic-paper use cases
// Summer Pre-Season · Corporate Accounts · Customer Segments · Shopee Search & Fulfillment ·
// Import Logistics Timeline · Last-Mile Routes · Stock Advisor · Shelf-Space · Network Rebalance · AP Optimization

import { BRANCHES, SKUS, SUPPLIERS } from "./data";

let _s = 42_000_001;
function rnd() { _s = (_s * 1664525 + 1013904223) % 4294967296; return _s / 4294967296; }
function r(a, b) { return a + rnd() * (b - a); }
function ri(a, b) { return Math.floor(r(a, b + 1)); }
function pick(arr) { return arr[Math.floor(rnd() * arr.length)]; }

/* =========================================================
   MERCHANDISING — Summer Pre-Season Planning
========================================================= */
const SUMMER_CRITICAL = SKUS.filter(s =>
  /Compressor|Freon|Capacitor|Condenser|Inverter|Auxiliary Fan|Blower/i.test(s.name)
).slice(0, 18);

export const SUMMER_PRESEASON = SUMMER_CRITICAL.map((s, i) => {
  const forecast = s.monthlyUnits * (3.2 + r(-0.4, 0.6)); // 12-week projection
  const onHand = Math.round(forecast * r(0.32, 0.78));
  const gap = Math.max(0, Math.round(forecast - onHand));
  const branchesShort = ri(6, 32);
  const leadTime = s.leadTimeDays;
  const orderBy = leadTime + 14; // days from today
  return {
    skuId: s.id, name: s.name, category: s.category, abc: s.abc,
    forecastUnits: Math.round(forecast),
    onHandUnits: onHand,
    gapUnits: gap,
    branchesShort,
    leadTimeDays: leadTime,
    orderByDays: orderBy,
    investment: gap * s.unitCost,
    risk: gap > forecast * 0.5 ? "critical" : gap > forecast * 0.25 ? "warning" : "info",
  };
}).sort((a, b) => b.investment - a.investment);

export const SUMMER_BRANCH_GAPS = (() => {
  const ncr = BRANCHES.filter(b => b.region === "Metro Manila").slice(0, 8);
  return ncr.map(b => ({
    branchId: b.id, name: b.name, region: b.region,
    coverWeeks: +(r(2.2, 6.4)).toFixed(1),
    skusShort: ri(8, 32),
    investmentNeeded: ri(420_000, 2_800_000),
  }));
})();

/* =========================================================
   OMNICHANNEL — Corporate Accounts B2B
========================================================= */
export const CORPORATE_ACCOUNTS = [
  { id: "ACC-001", name: "Metro Aircon Services Corp", segment: "HVAC Contractor", ytdRevenue: 18_400_000, ytdLastYr: 21_200_000, growth: -0.132, freq: 24, signal: "at-risk", action: "Schedule QBR; declining order frequency 6 → 4/qtr" },
  { id: "ACC-002", name: "FastCool Auto Detailing", segment: "Auto Repair Chain", ytdRevenue: 12_800_000, ytdLastYr: 10_900_000, growth: 0.174, freq: 38, signal: "upsell", action: "Pitch Sanden inverter bundle + extended warranty" },
  { id: "ACC-003", name: "Visayas Cold Storage Inc", segment: "Cold-Chain Operator", ytdRevenue: 8_240_000, ytdLastYr: 7_180_000, growth: 0.148, freq: 18, signal: "upsell", action: "Refrigerant supply contract Q2 renewal" },
  { id: "ACC-004", name: "Subic Logistics Park", segment: "Industrial Fleet", ytdRevenue: 6_180_000, ytdLastYr: 5_840_000, growth: 0.058, freq: 22, signal: "stable", action: "Maintain coverage, monitor lead-time complaints" },
  { id: "ACC-005", name: "Davao Fleet Services", segment: "Industrial Fleet", ytdRevenue: 4_920_000, ytdLastYr: 6_800_000, growth: -0.276, signal: "at-risk", freq: 14, action: "Win-back: pricing review + technical support visit" },
  { id: "ACC-006", name: "Cebu Marine Industries", segment: "Marine HVAC", ytdRevenue: 3_840_000, ytdLastYr: 2_100_000, growth: 0.829, freq: 12, signal: "new", action: "Onboarded Q3 2025 — accelerate account development" },
  { id: "ACC-007", name: "Pampanga Truck Center", segment: "Auto Repair Chain", ytdRevenue: 5_640_000, ytdLastYr: 5_180_000, growth: 0.089, freq: 28, signal: "stable", action: "Cross-sell consumables; basket low at ₱18K avg" },
  { id: "ACC-008", name: "Iloilo Cooling Solutions", segment: "HVAC Contractor", ytdRevenue: 2_180_000, ytdLastYr: 0, growth: null, freq: 8, signal: "new", action: "First-year ramp; assign dedicated AE" },
  { id: "ACC-009", name: "GMA Cargo Refrigeration", segment: "Cold-Chain Operator", ytdRevenue: 9_400_000, ytdLastYr: 8_120_000, growth: 0.158, freq: 21, signal: "upsell", action: "Reefer unit contract opportunity Q3" },
  { id: "ACC-010", name: "Quezon City Bus Lines", segment: "Public Transport", ytdRevenue: 7_280_000, ytdLastYr: 7_640_000, growth: -0.047, freq: 19, signal: "stable", action: "Monitor; minor decline within tolerance" },
];

/* =========================================================
   MARKETING — Customer Segments (RFM-style)
========================================================= */
export const CUSTOMER_SEGMENTS = [
  { id: "SEG-PRO", name: "Pro Mechanics & Repair Shops", size: 8_420, ytdRevenue: 184_000_000, basket: 4_280, freq: 22, channel: "Physical+Direct", action: "Loyalty: trade credit + bulk discount" },
  { id: "SEG-HVAC", name: "HVAC Contractors", size: 1_240, ytdRevenue: 142_000_000, basket: 18_400, freq: 14, channel: "Direct", action: "Account management; lead-gen via Shopee Business" },
  { id: "SEG-COLD", name: "Cold-Chain Operators", size: 312, ytdRevenue: 86_000_000, basket: 42_800, freq: 8, channel: "Direct", action: "Service contracts; refrigerant supply lock-in" },
  { id: "SEG-WALK", name: "Walk-in DIY", size: 42_800, ytdRevenue: 64_000_000, basket: 280, freq: 4, channel: "Physical+Shopee", action: "Shopee discoverability; consumables upsell" },
  { id: "SEG-FLEET", name: "Fleet Operators", size: 184, ytdRevenue: 38_000_000, basket: 28_400, freq: 12, channel: "Direct+Physical", action: "Bundled maintenance contracts" },
  { id: "SEG-RESELL", name: "Resellers / Subdistributors", size: 96, ytdRevenue: 28_000_000, basket: 124_000, freq: 6, channel: "Direct", action: "Distribution agreement renewals + territory mapping" },
];

/* =========================================================
   MARKETING — Shopee Search Trends (emerging queries)
========================================================= */
export const SHOPEE_SEARCH_TRENDS = [
  { query: "inverter aircon kompresor", searches: 12_840, growth: 0.84, hasListing: true, conversion: 0.062 },
  { query: "freon r32 9.5kg presyo", searches: 8_420, growth: 0.42, hasListing: true, conversion: 0.084 },
  { query: "aircon controller universal", searches: 6_180, growth: 0.62, hasListing: true, conversion: 0.038 },
  { query: "honda crv aircon compressor 2024", searches: 4_120, growth: 1.18, hasListing: false, conversion: 0 },
  { query: "anti-bacterial filter aircon", searches: 3_840, growth: 0.96, hasListing: true, conversion: 0.052 },
  { query: "ref door gasket 18 inches", searches: 3_240, growth: 0.24, hasListing: false, conversion: 0 },
  { query: "smart thermostat wifi", searches: 2_840, growth: 1.42, hasListing: false, conversion: 0 },
  { query: "r290 refrigerant 6kg eco", searches: 2_180, growth: 2.18, hasListing: true, conversion: 0.046 },
  { query: "blower motor adventure", searches: 1_840, growth: 0.32, hasListing: true, conversion: 0.062 },
  { query: "ev aircon compressor", searches: 1_640, growth: 3.42, hasListing: false, conversion: 0 },
];

/* =========================================================
   LOGISTICS — Shopee Order Fulfillment Hub queue
========================================================= */
export const FULFILLMENT_QUEUE = (() => {
  const statuses = ["picking", "packing", "ready", "dispatched"];
  return Array.from({ length: 24 }).map((_, i) => {
    const status = statuses[ri(0, statuses.length - 1)];
    const cutoff = ri(15, 480);
    const minutesLeft = cutoff - ri(0, 240);
    return {
      orderId: `SP-${ri(100000, 999999)}`,
      sku: pick(SKUS).name,
      units: ri(1, 6),
      value: ri(820, 18_400),
      status,
      cutoffMinutes: cutoff,
      minutesLeft,
      atRisk: minutesLeft < 60 && status !== "dispatched",
      hub: pick(["Pasig DC", "Mandaue DC", "Davao DC"]),
    };
  });
})();

export const FULFILLMENT_STATS = {
  ordersToday: 1_842,
  fillRate24h: 0.961,
  avgCycleMinutes: 142,
  returnRate: 0.034,
  atRiskCount: 14,
};

/* =========================================================
   PROCUREMENT — Import Logistics Timeline (vessel tracking)
========================================================= */
export const IMPORT_LOGISTICS = [
  {
    poId: "PO-2026-0148", supplier: "Sanden International", value: 8_240_000,
    skus: 4, units: 480,
    stages: [
      { stage: "Factory", planned: "2026-01-08", actual: "2026-01-10", status: "complete" },
      { stage: "Port Load", planned: "2026-01-22", actual: "2026-01-24", status: "complete" },
      { stage: "Vessel", planned: "2026-02-18", actual: "2026-02-22", status: "in-transit", vessel: "MV Kobe Express V.024N" },
      { stage: "Customs", planned: "2026-03-04", actual: null, status: "pending" },
      { stage: "DC Receipt", planned: "2026-03-14", actual: null, status: "pending" },
    ],
    etaConfidence: 0.78,
    delayRisk: 4,
  },
  {
    poId: "PO-2026-0149", supplier: "Capella Industrial", value: 12_480_000,
    skus: 7, units: 1_840,
    stages: [
      { stage: "Factory", planned: "2025-12-14", actual: "2026-01-04", status: "complete" },
      { stage: "Port Load", planned: "2026-01-04", actual: "2026-01-18", status: "complete" },
      { stage: "Vessel", planned: "2026-02-02", actual: "2026-02-22", status: "in-transit", vessel: "MV Shenzhen Star V.018E" },
      { stage: "Customs", planned: "2026-02-18", actual: null, status: "delayed" },
      { stage: "DC Receipt", planned: "2026-04-02", actual: null, status: "pending" },
    ],
    etaConfidence: 0.42,
    delayRisk: 18,
  },
  {
    poId: "PO-2026-0151", supplier: "Honeywell Refrigerants", value: 6_780_000,
    skus: 3, units: 1_200,
    stages: [
      { stage: "Factory", planned: "2026-01-22", actual: "2026-01-22", status: "complete" },
      { stage: "Port Load", planned: "2026-02-08", actual: "2026-02-08", status: "complete" },
      { stage: "Vessel", planned: "2026-03-12", actual: null, status: "in-transit", vessel: "MV Pacific Pearl V.142W" },
      { stage: "Customs", planned: "2026-03-28", actual: null, status: "pending" },
      { stage: "DC Receipt", planned: "2026-04-08", actual: null, status: "pending" },
    ],
    etaConfidence: 0.86,
    delayRisk: 1,
  },
  {
    poId: "PO-2026-0152", supplier: "Matsushita / Panasonic", value: 4_240_000,
    skus: 2, units: 320,
    stages: [
      { stage: "Factory", planned: "2026-02-04", actual: null, status: "in-production" },
      { stage: "Port Load", planned: "2026-02-22", actual: null, status: "pending" },
      { stage: "Vessel", planned: "2026-03-18", actual: null, status: "pending" },
      { stage: "Customs", planned: "2026-04-04", actual: null, status: "pending" },
      { stage: "DC Receipt", planned: "2026-04-12", actual: null, status: "pending" },
    ],
    etaConfidence: 0.92,
    delayRisk: 0,
  },
];

/* =========================================================
   LOGISTICS — Last-Mile Delivery Routes
========================================================= */
export const DELIVERY_ROUTES = [
  { id: "RT-001", vehicle: "Pasig Hub Van-04", driver: "R. Aquino", stops: 14, kmPlanned: 62, kmActual: 58, fuelL: 8.4, fuelSavedL: 1.8, eta: "16:42", status: "on-route", utilization: 0.82 },
  { id: "RT-002", vehicle: "Mandaue Hub Truck-02", driver: "L. Tan", stops: 18, kmPlanned: 84, kmActual: 92, fuelL: 14.6, fuelSavedL: -1.2, eta: "17:24", status: "on-route", utilization: 0.94 },
  { id: "RT-003", vehicle: "Davao Hub Van-01", driver: "K. Mendoza", stops: 11, kmPlanned: 48, kmActual: 46, fuelL: 6.8, fuelSavedL: 0.4, eta: "15:18", status: "on-route", utilization: 0.74 },
  { id: "RT-004", vehicle: "Pasig Hub Van-08", driver: "B. Cruz", stops: 16, kmPlanned: 74, kmActual: 71, fuelL: 10.2, fuelSavedL: 1.4, eta: "16:08", status: "delayed", utilization: 0.88 },
  { id: "RT-005", vehicle: "Cabanatuan Truck-01", driver: "M. Reyes", stops: 22, kmPlanned: 142, kmActual: 138, fuelL: 22.4, fuelSavedL: 2.8, eta: "18:32", status: "on-route", utilization: 0.96 },
  { id: "RT-006", vehicle: "Pasig Hub Van-12", driver: "E. Villanueva", stops: 12, kmPlanned: 56, kmActual: 54, fuelL: 7.6, fuelSavedL: 0.6, eta: "15:54", status: "complete", utilization: 0.71 },
];

export const ROUTE_SAVINGS = {
  fuelSavedL: 12.4,
  kmSaved: 38,
  timeSavedMin: 84,
  costSaved: 28_400,
  vsManualPlanning: 0.18,
};

/* =========================================================
   OPERATIONS — AI Stock Advisor daily action queue
========================================================= */
export const STOCK_ADVISOR_ACTIONS = [
  { id: "ACT-001", role: "Central Warehouse Manager", action: "Replenish", title: "Dispatch Freon R32 9.5kg to Cebu hub", units: 480, branches: 3, impact: 4_200_000, urgency: "critical", confidence: 0.94, reason: "Heat advisory + Shopee 4.4 promo arriving; current cover 6 days" },
  { id: "ACT-002", role: "Demand Planning Manager", action: "Add to PO", title: "Add 320u Sanden SD7H15 to open PO-2026-0148", units: 320, branches: 8, impact: 5_760_000, urgency: "critical", confidence: 0.91, reason: "Forecast revised +18%; current cover 11 days vs 75-day lead time" },
  { id: "ACT-003", role: "Branch Head — Mandaue", action: "Transfer In", title: "Receive 60u Inverter Compressor 1.5HP from Cebu City", units: 60, branches: 1, impact: 960_000, urgency: "warning", confidence: 0.87, reason: "Stockout in 4 days; Cebu City has 28-day excess" },
  { id: "ACT-004", role: "Branch Head — Davao", action: "Overstock Review", title: "Liquidate 142u Foam Filter Roll (142 days idle)", units: 142, branches: 1, impact: 184_000, urgency: "warning", confidence: 0.96, reason: "Working capital tied up; phase-out candidate" },
  { id: "ACT-005", role: "Central Warehouse Manager", action: "Expedite", title: "Air-freight Honda CR-V Aux Fan — corporate quote due", units: 24, branches: 0, impact: 480_000, urgency: "critical", confidence: 0.88, reason: "FastCool Auto Detailing quote for 24u due Friday; sea-freight ETA misses by 14d" },
  { id: "ACT-006", role: "Demand Planning Manager", action: "Review Forecast", title: "Auto category Q2 forecast trending +24% vs plan", units: 0, branches: 0, impact: 12_400_000, urgency: "info", confidence: 0.81, reason: "Heat-index forecast + LTO registration data signal sustained uplift" },
  { id: "ACT-007", role: "Regional Manager — Mindanao", action: "Branch Action", title: "Cotabato achievement 74% — request action plan", units: 0, branches: 1, impact: 1_840_000, urgency: "warning", confidence: 0.92, reason: "3-month underperformance, stockout > 8%, low foot-traffic" },
  { id: "ACT-008", role: "Merchandising Manager", action: "New SKU", title: "Approve EV Aircon Compressor (NPI-2026-002) sourcing", units: 0, branches: 0, impact: 8_400_000, urgency: "info", confidence: 0.78, reason: "Search trend +342% on Shopee; no SKU in catalog yet" },
];

/* =========================================================
   OPERATIONS — Shelf-Space & Staffing
========================================================= */
export const SHELF_SPACE_RECS = [
  { branchId: "BR-001", name: "Bacoor Cavite", currentMix: "Auto 42%/HVAC 28%/Refrig 18%/Cons 12%", recommendedMix: "Auto 38%/HVAC 34%/Refrig 18%/Cons 10%", upliftPct: 0.062, reason: "HVAC inverter demand rising; expand inverter compressor facing" },
  { branchId: "BR-014", name: "Mandaue Cebu", currentMix: "Auto 36%/HVAC 32%/Refrig 22%/Cons 10%", recommendedMix: "Auto 32%/HVAC 28%/Refrig 30%/Cons 10%", upliftPct: 0.084, reason: "Cold-chain cluster — refrigerant attach rate 2.4× network avg" },
  { branchId: "BR-026", name: "Calamba", currentMix: "Auto 48%/HVAC 24%/Refrig 16%/Cons 12%", recommendedMix: "Auto 44%/HVAC 26%/Refrig 18%/Cons 12%", upliftPct: 0.038, reason: "Light HVAC re-balance; rotate 6 slow-mover auto SKUs out" },
];

export const STAFFING_INSIGHTS = [
  { branchId: "BR-001", name: "Bacoor Cavite", txnPerStaffHour: 8.4, benchmark: 7.2, status: "above", note: "Top quartile" },
  { branchId: "BR-014", name: "Mandaue Cebu", txnPerStaffHour: 9.8, benchmark: 7.2, status: "above", note: "Sustain; consider opening 2nd checkout" },
  { branchId: "BR-076", name: "Calapan Mindoro", txnPerStaffHour: 4.2, benchmark: 7.2, status: "below", note: "Reduce schedule by 1 FTE on slow days (M/Tu)" },
  { branchId: "BR-068", name: "Vigan", txnPerStaffHour: 4.8, benchmark: 7.2, status: "below", note: "Cross-train; merge shifts" },
];

/* =========================================================
   OPERATIONS — Branch Network Rebalancing manifest
========================================================= */
export const REBALANCE_MANIFEST = [
  { id: "TF-001", sku: "Inverter Compressor 1.5HP", units: 60, from: "Cebu City", to: "Mandaue Cebu", costTransport: 8_400, valueMoved: 960_000, healthGain: 0.18 },
  { id: "TF-002", sku: "Freon R32 9.5kg", units: 24, from: "Pasig Ortigas", to: "Calapan Mindoro", costTransport: 14_200, valueMoved: 79_200, healthGain: 0.22 },
  { id: "TF-003", sku: "Sanden SD7H15 Compressor", units: 18, from: "Quezon City Commonwealth", to: "Cabanatuan East", costTransport: 6_800, valueMoved: 432_000, healthGain: 0.16 },
  { id: "TF-004", sku: "Capella Auto Compressor TM-16", units: 32, from: "Manila Quiapo", to: "Olongapo", costTransport: 9_200, valueMoved: 412_800, healthGain: 0.14 },
  { id: "TF-005", sku: "Foam Filter Roll", units: 142, from: "Davao City", to: "Cagayan de Oro", costTransport: 4_400, valueMoved: 184_600, healthGain: 0.08 },
  { id: "TF-006", sku: "Rotary Compressor 1.0HP", units: 28, from: "Bacoor Cavite", to: "Tagaytay", costTransport: 3_200, valueMoved: 364_000, healthGain: 0.12 },
  { id: "TF-007", sku: "Universal Aircon Control PCB", units: 48, from: "Pasay Roxas", to: "Lucena Diversion", costTransport: 5_400, valueMoved: 86_400, healthGain: 0.11 },
  { id: "TF-008", sku: "Refrig Door Gasket 24in", units: 84, from: "Marikina", to: "Iloilo City", costTransport: 11_200, valueMoved: 134_400, healthGain: 0.09 },
];

/* =========================================================
   FINANCE — AP / Working Capital Optimization
========================================================= */
export const AP_OPTIMIZATION = [
  { supplierId: "SUP-001", supplier: "Sanden International", invoiceValue: 8_240_000, currentTerm: "60 days LC", action: "Accept 2/10 net 60", savings: 164_800, dpoImpact: -10, riskScore: 0.18, recommend: "accept", reason: "Strategic supplier; cash discount > revolving credit cost" },
  { supplierId: "SUP-002", supplier: "Capella Industrial", invoiceValue: 12_480_000, currentTerm: "60 days LC", action: "Extend to Net-75", savings: 89_400, dpoImpact: +15, riskScore: 0.34, recommend: "negotiate", reason: "OTD 84%, multiple delays — leverage delay for term extension" },
  { supplierId: "SUP-003", supplier: "Matsushita / Panasonic", invoiceValue: 6_780_000, currentTerm: "90 days LC", action: "Hold — strategic", savings: 0, dpoImpact: 0, riskScore: 0.06, recommend: "hold", reason: "Strategic; OTD 95% rating 4.8 — don't disturb relationship" },
  { supplierId: "SUP-009", supplier: "Local Manila Distributors", invoiceValue: 1_240_000, currentTerm: "Net-30", action: "Accept 2/10", savings: 24_800, dpoImpact: -20, riskScore: 0.04, recommend: "accept", reason: "Local supplier, fast cycle, easy WC win" },
  { supplierId: "SUP-006", supplier: "Honeywell Refrigerants", invoiceValue: 4_840_000, currentTerm: "60 days LC", action: "Extend to Net-90", savings: 64_800, dpoImpact: +30, riskScore: 0.42, recommend: "negotiate", reason: "OTD 74% trending lower — supplier needs us, leverage term" },
  { supplierId: "SUP-005", supplier: "Daikin Parts Asia", invoiceValue: 5_240_000, currentTerm: "45 days LC", action: "Accept 1.5/10 net 45", savings: 78_600, dpoImpact: -8, riskScore: 0.22, recommend: "accept", reason: "Discount + relationship benefit" },
];

export const AP_SUMMARY = {
  totalOpenPayables: 38_820_000,
  earlyDiscountCapture: 332_200,
  termExtensionCash: 1_820_000,
  recommendedDpoTarget: 58,
  currentDpo: 51.4,
  workingCapitalUnlock: 2_152_000,
};
